import React from "react";

function Sk({ className = "" }) {
  return (
    <div className={["skeleton-premium", className].join(" ")} aria-hidden />
  );
}

/**
 * Premium loading shell mirroring Home layout (hero → projects → clients →
 * business → CTA → services → gifting).
 */
function HomeSkeleton() {
  return (
    <div
      className="min-h-screen bg-surface"
      aria-busy="true"
      aria-label="Loading home page"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full bg-brand-orange/[0.07] blur-[100px]" />
        <div className="absolute bottom-0 left-0 h-[360px] w-[360px] rounded-full bg-brand-amber/[0.05] blur-[90px]" />
      </div>

      {/* Hero */}
      <section className="relative min-h-[88vh] border-b border-white/[0.04]">
        <div className="container relative z-[1] mx-auto px-4 py-16 lg:py-20">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-14">
            <div className="max-w-xl space-y-5 lg:max-w-2xl lg:pl-2">
              <Sk className="h-3 w-48 rounded-full md:h-3.5 md:w-56" />
              <div className="space-y-3">
                <Sk className="h-10 w-full max-w-md sm:h-12 md:h-14" />
                <Sk className="h-10 w-full max-w-sm sm:h-12 md:h-14" />
              </div>
              <Sk className="h-1 w-32 rounded-full md:w-40" />
              <Sk className="h-5 w-64 rounded-full md:h-6 md:w-80" />
              <div className="flex gap-4 pt-2">
                <Sk className="h-24 flex-1 max-w-lg rounded-lg" />
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <Sk className="h-12 w-40 rounded-full" />
                <Sk className="h-12 w-44 rounded-full" />
              </div>
              <Sk className="h-10 w-56 rounded-full" />
            </div>

            <div className="relative mx-auto flex w-full max-w-md justify-center lg:max-w-none">
              <div className="relative w-full max-w-[320px] sm:max-w-[380px]">
                <div className="absolute -inset-3 rounded-[1.35rem] border border-white/[0.06]" />
                <Sk className="aspect-[3/5] w-full rounded-2xl shadow-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="border-t border-white/[0.06] py-14 md:py-16">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex flex-col justify-between gap-6 md:mb-10 md:flex-row md:items-end">
            <div className="space-y-3">
              <Sk className="h-3 w-28 rounded-full" />
              <Sk className="h-8 w-64 max-w-full md:h-10 md:w-80" />
              <Sk className="h-4 w-full max-w-md rounded-full" />
            </div>
            <div className="flex gap-2">
              <Sk className="h-11 w-11 rounded-full" />
              <Sk className="h-11 w-11 rounded-full" />
            </div>
          </div>
          <Sk className="mb-4 h-[min(52vw,420px)] w-full max-w-4xl rounded-2xl md:h-80 lg:mx-auto lg:h-96" />
          <div className="flex justify-center gap-2 md:gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Sk
                key={i}
                className="h-2 w-2 shrink-0 rounded-full md:h-2.5 md:w-2.5"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Clients marquee */}
      <section className="border-t border-white/[0.06] py-16 md:py-20">
        <div className="container mx-auto mb-12 px-4 md:mb-16">
          <div className="mx-auto flex justify-center">
            <Sk className="h-10 w-56 rounded-full md:h-12 md:w-72" />
          </div>
        </div>
        <div className="flex gap-6 overflow-hidden px-4 md:gap-10">
          {Array.from({ length: 10 }).map((_, i) => (
            <Sk
              key={i}
              className="h-20 w-[140px] shrink-0 rounded-xl sm:h-24 sm:w-[160px] md:h-28 md:w-[180px]"
            />
          ))}
        </div>
      </section>

      {/* Business / testimonials */}
      <section className="border-t border-white/[0.06] py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="mb-10 text-center md:mb-14">
            <Sk className="mx-auto mb-3 h-3 w-32 rounded-full" />
            <Sk className="mx-auto h-9 w-72 max-w-full rounded-lg md:h-11 md:w-96" />
          </div>
          <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Sk key={i} className="h-28 rounded-2xl md:h-32" />
            ))}
          </div>
          <div className="mx-auto mt-10 max-w-3xl space-y-4">
            <Sk className="h-4 w-full rounded-full" />
            <Sk className="h-4 w-[92%] rounded-full" />
            <Sk className="h-4 w-[78%] rounded-full" />
            <div className="flex items-center justify-between pt-4">
              <Sk className="h-10 w-40 rounded-full" />
              <div className="flex gap-2">
                <Sk className="h-10 w-10 rounded-full" />
                <Sk className="h-10 w-10 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative py-12 md:py-14">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/30 via-brand-amber/25 to-brand-yellow/25" />
        <div className="container relative z-[1] mx-auto px-4 py-6 text-center">
          <Sk className="mx-auto mb-3 h-3 w-44 rounded-full bg-black/20" />
          <Sk className="mx-auto mb-4 h-8 w-64 max-w-full rounded-lg bg-black/15 md:h-10 md:w-80" />
          <Sk className="mx-auto mb-6 h-4 w-full max-w-lg rounded-full bg-black/15" />
          <Sk className="mx-auto h-12 w-48 rounded-full bg-black/25" />
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-white/[0.06] py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center md:mb-16">
            <Sk className="mx-auto mb-3 h-3 w-36 rounded-full" />
            <Sk className="mx-auto h-10 w-80 max-w-full rounded-xl md:h-12 md:w-96" />
            <Sk className="mx-auto mt-4 h-4 w-full max-w-2xl rounded-full" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3 rounded-2xl border border-white/[0.06] bg-surface-elevated/40 p-4">
                <Sk className="aspect-[4/3] w-full rounded-xl" />
                <Sk className="h-5 w-3/4 max-w-[200px] rounded-full" />
                <Sk className="h-3 w-full rounded-full" />
                <Sk className="h-3 w-[88%] rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gifting */}
      <section className="border-t border-white/[0.06] py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 flex flex-col items-center text-center md:mb-16">
            <Sk className="mb-3 h-3 w-32 rounded-full" />
            <Sk className="h-10 w-72 max-w-full rounded-xl md:h-12 md:w-96" />
            <Sk className="mt-4 h-4 w-full max-w-xl rounded-full" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3 rounded-2xl border border-white/[0.06] bg-surface-elevated/40 p-4">
                <Sk className="aspect-square w-full rounded-xl" />
                <Sk className="h-5 w-2/3 rounded-full" />
                <Sk className="h-3 w-full rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomeSkeleton;
